import { Box, Divider, Grid, Switch, Typography, useTheme } from '@mui/material';
import PageHeader from 'components/Header/Page';
import React, { useEffect, useState } from 'react';
import LoaderComponent from 'components/Loader';
import Insights from './Insights';
import IncidentReportIcon from '../../assets/Incident_Report.svg';
import { addOrUpdateInsights, deleteInsights, getInsights, fetchMimsMetrics, fetchMimsMetricTrend, fetchProblemTicketsMetricTrend, fetchProblemTecketsMetrics, fetchIncidentsSummary } from 'service/service';
import moment from 'moment';
import MultiLineWidget from 'components/Widgets/MultiLineChart';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import dayjs from 'dayjs';
import RowTypography from 'components/Table/RowTypography';
import CaptureExportControls from 'utils/CaptureExportControls';
import IncidentFilter from 'components/Dropdown/IncidentFilter';
import PageWrapper from 'components/Common/PageWraper';
import PieInfoWidget from 'components/Widgets/PieInfoWidget';
import SectorFilter from 'components/Dropdown/SectorFilter';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import YearMonthPicker from 'components/Dropdown/YearMonthPicker';
import { SECTOR_OPTIONS } from 'utils/xopsUtil';

const MIMReports = () => {
    const theme = useTheme();

    const [sectorName, setSectorName] = useState(() => {
        const storedValue = localStorage.getItem("xopsMIMReportsSector");
        return storedValue === null || storedValue === undefined ? "all" : storedValue;
    });

    const isXOps = true;

    const getInitialMonth = () => {
        return isXOps ? moment().subtract(1, 'month').format('YYYY-MM') : '0M';
    };

    const [customMonth, setCustomMonth] = useState(getInitialMonth());
    const [filterMonth, setFilterMonth] = useState(getInitialMonth());

    const [insights, setInsights] = useState<Insights[]>([]);
    const [loader, setLoader] = useState<boolean>(false);
    const [gridLoader, setGridLoader] = useState<boolean>(false);
    const [mimsMetricChartLoader, SetMimsMetricChartLoader] = useState<boolean>(true);
    const [mimsMetricChartOptions, SetMimsMetricChartOptions] = useState<Record<string, any>>({});
    const [tableData, setTableData] = useState<Row[]>([]);
    const [rawMimstableData, setRawMimsTableData] = useState<Row[]>([]);
    const [sortingInfo] = useState({ sortBy: '', sortDir: '' });
    const [chartPreference, setChartPreference] = useState('all');
    const [mimsRawData, setMimsRawData] = useState<{ currentYearIncident: any[], lastYearIncident: any[] }>({
        currentYearIncident: [],
        lastYearIncident: []
    });
    const [, setSelectedMimsType] = useState<'all' | 'Actual MIMs' | 'Preventive MIMs'>('all');

    const [problemMetricChartLoader, SetProblemMetricChartLoader] = useState<boolean>(true);
    const [problemMetricChartOptions, SetProblemMetricChartOptions] = useState<Record<string, any>>({});
    const [problemTableData, setProblemTableData] = useState<Row[]>([]);
    const [problemGridLoader, setProblemGridLoader] = useState<boolean>(false);
    const [problemSortingInfo] = useState({ sortBy: '', sortDir: '' });
    const [widgetData, setWidgetData] = useState<any[]>([]);
    const [chartLoading, setChartLoading] = useState(true);
    const [majorProblemBacklogData, setMajorProblemBacklogData] = useState<PieChartItem[]>([]);
    const [, setMajorProblemBacklogLoading] = useState(true);
    const [sectorOptionsList] = useState<any[]>(SECTOR_OPTIONS);
    const [isClosed, setIsClosed] = useState(true);
    const [isExportingMims, setIsExportingMims] = useState<boolean>(false);
    const [isExportingProblems, setIsExportingProblems] = useState<boolean>(false);

    const mimColorMapping = {
        'MIMs': '#299BFF',
        'Preventive MIMs': '#01C008'
    };

    const p3IncidentsColorMapping = {
        'Databricks': '#299BFF',
        'PowerBI': '#FFC231',
        'Teradata': '#00D08D',
        'Tableau': '#FD783D',
        'Presto': '#8884D8',
        'Others': '#707070'
    };

    const majorProblemBacklogColorMapping = {
        'Root Cause Analysis': '#299BFF',
        'Fix In Progress': '#01C008',
        'Closed': '#FFA200'
    };

    interface PieChartItem {
        name: string;
        value: number;
        formattedName: string;
    }

    interface Insights {
        insightId: number;
        title: string;
        description: string;
        opsType: string;
        createdBy: string;
        updatedBy: string;
        createdDate: string;
        updatedDate: string;
    }

    interface Row {
        Technology: string;
        AssignmentGroup: number;
        PlatformImpact: string;
        OutageDuration: number;
        OutageType: number;
        Incident_Id: string;
        Problem_Id: string;
        MTTR_RCA: string;
    }

    const defaultColumnNames = [
        'Incident Id',
        'Technology',
        'Assignment Group',
        'Platform Impact',
        'Problem Id',
        'Outage Duration (Min)',
        'Outage Type',
    ];

    const problemMetricsDefaultColumnNames = [
        'Problem Id',
        'Technology',
        'Assignment Group',
        'Current Status',
        'Aging',
        'MTTR RCA (Hrs)'
    ];

    const getColumns = () => {
        const columnMap: Array<{ key: string; label: string }> = [
            { key: 'Incident_Id', label: 'Incident Id' },
            { key: 'Technology', label: 'Technology' },
            { key: 'AssignmentGroup', label: 'Assignment Group' },
            { key: 'PlatformImpact', label: 'Platform Impact' },
            { key: 'Problem_Id', label: 'Problem Id' },
            { key: 'OutageDuration', label: 'Outage Duration' },
            { key: 'OutageType', label: 'Outage Type' },
        ];

        // Fields to append "%" and "Hrs"
        const hourFields = ['MTTR_RCA'];
        const minFields = ['OutageDuration']

        return columnMap.map(col => {
            // Add unit to header label
            let label = col.label;
            if (hourFields.includes(col.key)) {
                label += ' (Hrs)';
            } else if (minFields.includes(col.key)) {
                label += ' (Min)';
            }

            return {
                key: col.key,
                name: label,
                resizable: true,
                width: col.key === 'Problem_Id' || 'Incident_Id' || 'PlatformImpact' || 'OutageDuration' || 'OutageType' ? 'auto' : '230px',
                formatter: ({ row }: any) => {
                    let value = row[col.key];
                    if (hourFields.includes(col.key)) {
                        value = `${value} Hrs`;
                    } else if (minFields.includes(col.key)) {
                        value = `${value} Min`;
                    }
                    return <RowTypography>{value}</RowTypography>;
                }
            }
        })
    };

    const getProblemColumns = () => {
        const columnMap: Array<{ key: string; label: string }> = [
            { key: 'Problem_Id', label: 'Problem Id' },
            { key: 'Technology', label: 'Technology' },
            { key: 'AssignmentGroup', label: 'Assignment Group' },
            { key: 'CurrentStatus', label: 'Current Status' },
            { key: 'Aging', label: 'Aging' },
            { key: 'MTTR_RCA', label: 'MTTR RCA' }
        ];

        // Fields to append "%" and "Hrs"
        const hourFields = ['MTTR_RCA'];
        const minFields = ['OutageDuration']

        return columnMap.map(col => {
            // Add unit to header label
            let label = col.label;
            if (hourFields.includes(col.key)) {
                label += ' (Hrs)';
            } else if (minFields.includes(col.key)) {
                label += ' (Min)';
            }

            return {
                key: col.key,
                name: label,
                resizable: true,
                width: '230px',
                formatter: ({ row }: any) => {
                    let value = row[col.key];
                    if (hourFields.includes(col.key)) {
                        value = `${value} Hrs`;
                    } else if (minFields.includes(col.key)) {
                        value = `${value} Min`;
                    }
                    return <RowTypography>{value}</RowTypography>;
                }
            }
        })
    };

    async function fetchChartData(selectedDate: any, isClosedDate: any, timezoneFilter: string, sector: string) {
        try {
            setChartLoading(true);
            const res = await fetchIncidentsSummary(selectedDate, isClosedDate, timezoneFilter, sector);
            if (res.success) {
                const result = transformData(res.data);
                setWidgetData(result);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setChartLoading(false);
        }
    }

    // Function to transform response data into widget-friendly format
    const transformData = (response: any) => {
        const data = response?.data || response || {};
        let widgetData: any[] = [];

        if (data.mim?.length > 0) {
            const item = data.mim[0];
            const change = item.lastMonthCount === 0
                ? (item.currentMonthCount > 0 ? 100 : 0)
                : ((item.currentMonthCount - item.lastMonthCount) / item.lastMonthCount) * 100;
            const chartData = [
                { name: 'MIMs', value: item.mimsCount, formattedName: `${item.mimsCount || 0}` },
                { name: 'Preventive MIMs', value: item.preventiveCount, formattedName: `${item.preventiveCount || 0}` }
            ];

            widgetData.push({
                title: 'MIMs',
                value: item.totalCount,
                data: chartData,
                change: Math.abs(Number(change.toFixed(2))),
                changeType: item.currentMonthCount > item.lastMonthCount ? 'increase' : 'decrease'
            });
        }

        if (data.majorProblemBacklog?.length > 0) {
            const item = data.majorProblemBacklog[0];
            const total = item.rcaCount + item.fixInProgressCount + item.closedCount + item.newCount;

            const backlogData = [
                { name: 'Root Cause Analysis', value: item.rcaCount, formattedName: `${item.rcaCount}` },
                { name: 'Fix In Progress', value: item.fixInProgressCount, formattedName: `${item.fixInProgressCount}` },
                { name: 'Closed', value: item.closedCount, formattedName: `${item.closedCount}` },
                { name: 'New', value: item.newCount, formattedName: `${item.newCount}` }
            ];

            setMajorProblemBacklogData(backlogData.sort((a, b) => b.name.localeCompare(a.name)));
            setMajorProblemBacklogLoading(false);
        }
        return widgetData;
    };

    //Function to fetch insights
    const fetchInsights = async (selectedMonth: string) => {
        setLoader(true);
        try {
            const response = await getInsights('MIM Reports', '', selectedMonth);
            if (response.success) {
                setInsights(response.data.data);
                setLoader(false);
            } else {
                setLoader(false);
            }
        } catch (error) {
            console.error('Failed to get insights:', error);
        }
    };

    // Fetch insights
    useEffect(() => {
        fetchMimsMetricChartData(sectorName, isClosed, filterMonth);
        fetchProblemMetricChartData(sectorName, isClosed, filterMonth);
        listTableData({ selectedMonth: filterMonth, sector: sectorName, isClosedDate: isClosed });
        listProblemTableData({ selectedMonth: filterMonth, sector: sectorName, isClosedDate: isClosed });
        fetchInsights(filterMonth);
        fetchChartData(filterMonth, isClosed, 'UTC', sectorName);
    }, [isClosed, filterMonth]);

    // Function to handle adding a new insight
    const handleAddInsight = async (newInsight: any) => {
        try {
            setLoader(true);
            const response = await addOrUpdateInsights({
                title: newInsight?.title,
                xopsType: 'MIM Reports',
                description: newInsight?.description,
                month: filterMonth
            });
            if (response.success) {
                fetchInsights(filterMonth);
            } else {
                setLoader(false);
            }
        } catch (error) {
            console.error('Failed to add insight:', error);
        }
    };

    // Function to handle updating an insight
    const handleUpdateInsight = async (updatedInsight: any) => {
        try {
            setLoader(true);
            const response = await addOrUpdateInsights({
                title: updatedInsight?.title,
                xopsType: 'MIM Reports',
                description: updatedInsight?.description,
                insight_id: updatedInsight?.id,
                month: filterMonth
            });
            if (response.success) {
                fetchInsights(filterMonth);
            } else {
                setLoader(false);
            }
        } catch (error) {
            console.error('Failed to update insight:', error);
        }
    };

    // Function to handle deleting an insight
    const handleDeleteInsight = async (insightId: number) => {
        try {
            setLoader(true);
            const response = await deleteInsights(insightId);
            if (response.success) {
                fetchInsights(filterMonth);
            } else {
                setLoader(false);
            }
        } catch (error) {
            console.error('Failed to delete insight:', error);
        }
    };

    const handleDateChange = (selectedDate: string) => {
        listTableData({ selectedMonth: selectedDate, sector: sectorName });
        listProblemTableData({ selectedMonth: selectedDate, sector: sectorName });
        fetchInsights(selectedDate);
        fetchChartData(selectedDate, isClosed, 'UTC', sectorName);
        fetchMimsMetricChartData(sectorName, isClosed, selectedDate);
        fetchProblemMetricChartData(sectorName, isClosed, selectedDate);
    };

    const handleSorting = (sortingInfo: any) => { };

    // Fetch Incident Data
    const fetchMimsMetricChartData = async (sector: string, isClosedDate: any, filterMonth: any) => {
        try {
            SetMimsMetricChartLoader(true);
            const res = await fetchMimsMetricTrend(sector, isClosedDate, filterMonth);
            if (res.success) {
                setMimsRawData(res.data);
                const options = buildMimsChartOption(res.data.currentYearIncident, res.data.lastYearIncident, 'all');
                SetMimsMetricChartOptions(options);
            }
        } catch (error) {
            console.error('Error fetching incident trend data:', error);
        } finally {
            SetMimsMetricChartLoader(false);
        }
    };

    const fetchProblemMetricChartData = async (sector: string, isClosedDate: any, filterMonth: any) => {
        try {
            SetProblemMetricChartLoader(true);
            const res = await fetchProblemTicketsMetricTrend(sector, isClosedDate, filterMonth);
            if (res.success) {
                const options = buildProblemChartOption(res.data.currentYearIncident, res.data.lastYearIncident);
                SetProblemMetricChartOptions(options);
            }
        } catch (error) {
            console.error('Error fetching problem tickets trend data:', error);
        } finally {
            SetProblemMetricChartLoader(false);
        }
    };

    // Convert "2025-02" → "Feb 2025"
    const getMonthLabel = (yearMonth: string) => {
        const isYearFilter = filterMonth?.split('-')?.filter(df => !!df)?.length === 1
        return isYearFilter ? filterMonth : dayjs(`${yearMonth}-01`).format("MMM YYYY");
    };

    const buildMimsChartOption = (
        mimsData: any[],
        avgData: any[],
        mimsType: 'all' | 'Actual MIMs' | 'Preventive MIMs'
    ): Record<string, any> => {
        const mims = mimsData.map(item => item.mimsCount);
        const preventiveMims = mimsData.map(item => item.preventiveCount);
        const actualMims = mims.map((val, i) => val - preventiveMims[i]);

        const avgValue = avgData[0]?.avg ?? 0;
        const preventiveAvg = avgData[0]?.preventiveAvg ?? 0;
        const actualAvg = avgValue - preventiveAvg;

        const avgSeries = mimsData.map(() =>
            mimsType === 'Preventive MIMs' ? preventiveAvg :
                mimsType === 'Actual MIMs' ? actualAvg :
                    avgValue
        );

        const dates = mimsData.map(item => getMonthLabel(item.yearMonth));

        const dataSeries: any[] = [];

        // First line: primary series
        if (mimsType === 'all') {
            dataSeries.push({
                name: 'MIMs',
                type: 'line',
                data: mims,
                symbol: 'circle',
                color: '#009BD8',
                lineStyle: { width: 3 },
                label: { show: true, position: 'top', fontSize: 12, color: '#F0F0F0' },
                areaStyle: {
                    color: {
                        type: 'linear',
                        x: 0, y: 1, x2: 0, y2: 0,
                        colorStops: [
                            { offset: 0, color: '#053367' },
                            { offset: 1, color: '#009BD8' }
                        ]
                    }
                }
            });
        } else if (mimsType === 'Actual MIMs') {
            dataSeries.push({
                name: 'Actual MIMs',
                type: 'line',
                data: actualMims,
                symbol: 'circle',
                color: '#009BD8',
                lineStyle: { width: 3 },
                label: { show: true, position: 'top', fontSize: 12, color: '#F0F0F0' },
                areaStyle: {
                    color: {
                        type: 'linear',
                        x: 0, y: 1, x2: 0, y2: 0,
                        colorStops: [
                            { offset: 0, color: '#053367' },
                            { offset: 1, color: '#009BD8' }
                        ]
                    }
                }
            });
        } else if (mimsType === 'Preventive MIMs') {
            dataSeries.push({
                name: 'Preventive MIMs',
                type: 'line',
                data: preventiveMims,
                symbol: 'circle',
                color: '#9BD800',
                lineStyle: { width: 3 },
                label: { show: true, position: 'top', fontSize: 12, color: '#F0F0F0' },
                areaStyle: {
                    color: {
                        type: 'linear',
                        x: 0, y: 1, x2: 0, y2: 0,
                        colorStops: [
                            { offset: 0, color: '#053367' },
                            { offset: 1, color: '#9BD800' }
                        ]
                    }
                }
            });
        }

        // Second line: average series
        dataSeries.push({
            name:
                mimsType === 'Preventive MIMs' ? `${moment(filterMonth).year() - 1} Avg Preventive MIMs` :
                    mimsType === 'Actual MIMs' ? `${moment(filterMonth).year() - 1} Avg Actual MIMs` :
                        `${moment(filterMonth).year() - 1} Avg MIMs`,
            type: 'line',
            data: avgSeries,
            symbol: 'none',
            color:
                mimsType === 'Preventive MIMs' ? '#99C2FF' :
                    mimsType === 'Actual MIMs' ? '#FFC239' :
                        '#BBBBBB',
            lineStyle: { width: 2 }
        });

        const allValues = dataSeries.flatMap(s => s.data);
        const maxVal = Math.max(...allValues);
        const minVal = Math.min(...allValues);
        const yMax = Math.ceil(maxVal / 10) * 10;
        const yMin = Math.max(0, Math.floor(minVal / 10) * 10);

        return {
            backgroundColor: '#003771',
            tooltip: {
                trigger: 'axis',
                textStyle: { color: '#A7ADCB' },
                confine: true,
                backgroundColor: '#003771',
                formatter: function (params: any[]) {
                    const date = params[0]?.axisValueLabel;
                    let tooltipContent = `<div><strong>Date:</strong> ${date}</div>`;
                    params.forEach((item) => {
                        tooltipContent += `<div><strong>${item.seriesName}</strong>: ${item.value}</div>`;
                    });
                    return tooltipContent;
                }
            },
            grid: { top: '7%' },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: dates,
                axisLabel: { color: '#A7ADCB' },
                splitLine: { show: false }
            },
            yAxis: {
                type: 'value',
                min: yMin,
                max: yMax,
                axisLabel: {
                    color: '#A7ADCB',
                    formatter: (value: number) => (value >= 1000 ? `${value / 1000}k` : value)
                },
                splitLine: { show: false }
            },
            series: dataSeries,
            legend: {
                orient: 'horizontal',
                bottom: '1%',
                left: '2%',
                textStyle: { color: '#A7ADCB' },
                selectedMode: false,
                itemWidth: 10,
                itemHeight: 10,
                icon: 'roundRect'
            }
        };
    };

    const buildProblemChartOption = (
        problemData: any[],
        avgData: any[]
    ): Record<string, any> => {
        const problemTickets = problemData.map(item => item.problemTicketsCount);
        const avgValue = avgData[0]?.avg ?? 0;
        const avgSeries = problemData.map(() =>
            avgValue
        );
        const dates = problemData.map(item => getMonthLabel(item.yearMonth));

        const dataSeries: any[] = [];

        // First line: primary series
        dataSeries.push({
            name: 'Problem Tickets',
            type: 'line',
            data: problemTickets,
            symbol: 'circle',
            color: '#009BD8',
            lineStyle: { width: 3 },
            label: { show: true, position: 'top', fontSize: 12, color: '#F0F0F0' },
            areaStyle: {
                color: {
                    type: 'linear',
                    x: 0, y: 1, x2: 0, y2: 0,
                    colorStops: [
                        { offset: 0, color: '#053367' },
                        { offset: 1, color: '#009BD8' }
                    ]
                }
            }
        });

        // Second line: average series
        dataSeries.push({
            name: `${moment(filterMonth).year() - 1} Avg Problem Tickets`,
            type: 'line',
            data: avgSeries,
            symbol: 'none',
            color: '#BBBBBB',
            lineStyle: { width: 2 }
        });

        const allValues = dataSeries.flatMap(s => s.data);
        const Max = Math.max(...allValues)
        const Min = Math.min(...allValues)

        const yMax = Math.ceil(Max / 10) * 10;
        const yMin = Math.max(0, Math.floor(Min / 10) * 10);

        return {
            backgroundColor: '#003771',
            tooltip: {
                trigger: 'axis',
                textStyle: { color: '#A7ADCB' },
                confine: true,
                backgroundColor: '#003771',
                formatter: function (params: any[]) {
                    const date = params[0]?.axisValueLabel;
                    let tooltipContent = `<div><strong>Date:</strong> ${date}</div>`;
                    params.forEach((item) => {
                        tooltipContent += `<div><strong>${item.seriesName}</strong>: ${item.value}</div>`;
                    });
                    return tooltipContent;
                }
            },
            grid: { top: '7%' },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: dates,
                axisLabel: { color: '#A7ADCB' },
                splitLine: { show: false }
            },
            yAxis: {
                type: 'value',
                min: yMin,
                max: yMax,
                axisLabel: {
                    color: '#A7ADCB',
                    formatter: (value: number) => (value >= 1000 ? `${value / 1000}k` : value)
                },
                splitLine: { show: false }
            },
            series: dataSeries,
            legend: {
                orient: 'horizontal',
                bottom: '1%',
                left: '2%',
                textStyle: { color: '#A7ADCB' },
                selectedMode: false,
                itemWidth: 10,
                itemHeight: 10,
                icon: 'roundRect'
            }
        };
    };

    async function listTableData({ selectedMonth, sector, isClosedDate }: any) {
        try {
            setGridLoader(true);
            const res = await fetchMimsMetrics({
                selectedMonth,
                sector,
                isClosedDate
            });

            if (res.success) {
                setRawMimsTableData(res.data);
                const mimsData = filterMimDataByPreventive(res.data)
                setTableData(mimsData);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setGridLoader(false);
        }
    }

    const filterMimDataByPreventive = (data: any[] = [], filterType = 'all') => {
        if (!Array.isArray(data)) return [];

        if (filterType === 'Preventive MIMs') {
            return data.filter(item => item?.Preventive?.toLowerCase() === 'yes');
        }

        if (filterType === 'Actual MIMs') {
            return data.filter(item => item?.Preventive?.toLowerCase() === 'no');
        }

        return data;
    };

    async function listProblemTableData({ selectedMonth, sector, isClosedDate }: any) {
        try {
            setProblemGridLoader(true);
            const res = await fetchProblemTecketsMetrics({
                selectedMonth,
                sector,
                isClosedDate
            });

            if (res.success) {
                setProblemTableData(res.data);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setProblemGridLoader(false);
        }
    }

    const handleFilterChange = (option: string) => {
        if (option === 'all' || option === 'Actual MIMs' || option === 'Preventive MIMs') {
            setSelectedMimsType(option);
            const options = buildMimsChartOption(
                mimsRawData.currentYearIncident,
                mimsRawData.lastYearIncident,
                option
            );
            SetMimsMetricChartOptions(options);
            const mimsTableData = filterMimDataByPreventive(rawMimstableData, option)
            setTableData(mimsTableData)
        } else {
            console.warn('Invalid MIMs type selected:', option);
        }
    };

    const handleSectorChange = (value: string) => {
        setSectorName(value);
        localStorage.setItem('xopsMIMReportsSector', value)
        fetchMimsMetricChartData(value, isClosed, filterMonth);
        fetchProblemMetricChartData(value, isClosed, filterMonth);
        listTableData({ selectedMonth: filterMonth, sector: value, isClosedDate: isClosed });
        listProblemTableData({ selectedMonth: filterMonth, sector: value, isClosedDate: isClosed });
        fetchChartData(filterMonth, isClosed, 'UTC', value);
    };

    // Helper component to add context to titles
    const TitleWithContext = ({ title }: { title: string }) => (
        // This Box wrapper is the fix. It groups the title and context
        // so they are treated as a single element by any parent flex layouts.
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'nowrap' }}>
            <HighlightedText text={title} />
            <Typography
                variant="caption"
                component="span"
                sx={{
                    color: '#A7ADCB',
                    fontWeight: 'normal',
                    whiteSpace: 'nowrap'
                }}
            >
                ({sectorName === 'all' ? 'All Sectors' : sectorName} | {getMonthLabel(filterMonth)})
            </Typography>
        </Box>
    );

    const handleOpenClosedFilterChange = (event: any) => {
        const isClosed = event.target.checked;
        setIsClosed(isClosed);
    };

    const handleExportMims = () => {
        if (isExportingMims) return;
        setIsExportingMims(true);
        try {
            const tableType = 'Major_Incidents_Metrics';
            const storedPref = localStorage.getItem(`${tableType}_user_col_pref`);
            const storedColumnNames: string[] | null = storedPref ? JSON.parse(storedPref) : null;
            const preferredColumnNames: string[] = storedColumnNames?.length
                ? storedColumnNames
                : defaultColumnNames;

            const allColumns = getColumns();
            const nameToKey: Record<string, string> = {};
            allColumns.forEach((col: any) => { nameToKey[col.name] = col.key; });

            const exportData = tableData.map((row: any) => {
                const exportRow: Record<string, any> = {};
                preferredColumnNames.forEach((colName: string) => {
                    const key = nameToKey[colName];
                    if (key !== undefined) {
                        const val = row[key];
                        exportRow[colName] = val ?? '';
                    }
                });
                return exportRow;
            });

            const escapeCsv = (v: any) => `"${String(v ?? '').replaceAll('"', '""')}"`;
            const header = preferredColumnNames.filter(n => nameToKey[n] !== undefined).join(',');
            const rows = exportData.map((r: any) =>
                preferredColumnNames
                    .filter((n: string) => nameToKey[n] !== undefined)
                    .map((n: string) => escapeCsv(r[n]))
                    .join(',')
            );
            const csv = [header, ...rows].join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `MajorIncidents_Metrics_${filterMonth}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Export failed:', error);
        } finally {
            setIsExportingMims(false);
        }
    };

    const handleExportProblems = () => {
        if (isExportingProblems) return;
        setIsExportingProblems(true);
        try {
            const tableType = 'Major_Problem_Tickets_Metrics';
            const storedPref = localStorage.getItem(`${tableType}_user_col_pref`);
            const storedColumnNames: string[] | null = storedPref ? JSON.parse(storedPref) : null;
            const preferredColumnNames: string[] = storedColumnNames?.length
                ? storedColumnNames
                : problemMetricsDefaultColumnNames;

            const allColumns = getProblemColumns();
            const nameToKey: Record<string, string> = {};
            allColumns.forEach((col: any) => { nameToKey[col.name] = col.key; });

            const exportData = problemTableData.map((row: any) => {
                const exportRow: Record<string, any> = {};
                preferredColumnNames.forEach((colName: string) => {
                    const key = nameToKey[colName];
                    if (key !== undefined) {
                        const val = row[key];
                        exportRow[colName] = val ?? '';
                    }
                });
                return exportRow;
            });

            const escapeCsv = (v: any) => `"${String(v ?? '').replaceAll('"', '""')}"`;
            const header = preferredColumnNames.filter(n => nameToKey[n] !== undefined).join(',');
            const rows = exportData.map((r: any) =>
                preferredColumnNames
                    .filter((n: string) => nameToKey[n] !== undefined)
                    .map((n: string) => escapeCsv(r[n]))
                    .join(',')
            );
            const csv = [header, ...rows].join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `MajorProblemTickets_Metrics_${filterMonth}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Export failed:', error);
        } finally {
            setIsExportingProblems(false);
        }
    };

    return (
        <PageWrapper>
            <PageHeader title="MIM Reports " icon={IncidentReportIcon} >
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography fontSize='16px' fontWeight={600} color={theme.palette.textLight.dark}>Metrics by Open Date</Typography>
                    <Switch
                        checked={isClosed}
                        onChange={handleOpenClosedFilterChange}
                        size="small"
                        sx={{
                            width: 44, height: 22, padding: 0,
                            '& .MuiSwitch-switchBase': {
                                '&.Mui-checked': {
                                    transform: 'translateX(22px)',
                                    '& + .MuiSwitch-track': { backgroundColor: '#71758F', opacity: 1, },
                                    '& .MuiSwitch-thumb': { backgroundColor: 'white.main', },
                                },
                            },
                            '& .MuiSwitch-thumb': { width: 14, height: 14, backgroundColor: 'backgroundLight.dark', boxShadow: 'none', },
                            '& .MuiSwitch-track': { borderRadius: 14, backgroundColor: '#71758F', opacity: 1, },
                        }}
                    />
                    <Typography fontSize='16px' fontWeight={600} color={theme.palette.textLight.dark}>Metrics by Closed Date</Typography>
                </Box>
                <SectorFilter
                    sectorName={sectorName}
                    setSectorName={setSectorName}
                    sectorOptionsList={sectorOptionsList}
                    handleChange={handleSectorChange}
                    hasNotProvided={true}
                    boxProps={{
                        sx: { position: 'static', mr: 1 }
                    }}
                />
                <YearMonthPicker
                    customMonth={customMonth}
                    filterMonth={filterMonth}
                    setCustomMonth={setCustomMonth}
                    setFilterMonth={setFilterMonth}
                    handleChange={handleDateChange}
                />
                {/* <DateFilterDropdown
                    customMonth={customMonth}
                    filterMonth={filterMonth}
                    setCustomMonth={setCustomMonth}
                    setFilterMonth={setFilterMonth}
                    handleChange={handleDateChange}
                    isXOps={true}
                /> */}
            </PageHeader>

            <Grid container spacing={2} marginTop="1rem" px={{ xs: 1, md: 2, lg: 3 }}>
                {widgetData.map((widget, index) => (
                    <Grid item xs={12} md={6} lg={6} key={index}>
                        <PieInfoWidget
                            title={<TitleWithContext title={widget.title} />}
                            value={widget.value}
                            change={widget.change}
                            changeType={widget.changeType}
                            isLoading={chartLoading}
                            seriesData={widget.data}
                            customSize="200px"
                            showDollar={false}
                            colorMapping={widget.title === 'P3 Incidents' ? p3IncidentsColorMapping : mimColorMapping}
                            enableSectorCut={false}
                            setEnableSectorCut={false}
                        />
                    </Grid>
                ))}
                <Grid item xs={12} md={6} lg={6} id="major-problem-backlog">
                    <PieInfoWidget
                        title={<TitleWithContext title="Major Problem Backlog" />}
                        value={majorProblemBacklogData.reduce((sum, a) => sum + a.value, 0)}
                        change={0}
                        changeType={''}
                        isLoading={chartLoading}
                        seriesData={majorProblemBacklogData}
                        customSize="230px"
                        showDollar={false}
                        colorMapping={majorProblemBacklogColorMapping}
                        enableSectorCut={false}
                        setEnableSectorCut={false}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={1} marginTop="1rem" px={{ xs: 1, md: 2, lg: 3 }}>
                <Grid item xs={12} md={4} lg={4} id="mims" sx={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
                    <MultiLineWidget
                        title={<TitleWithContext title="MIMs" />}
                        isLoading={mimsMetricChartLoader}
                        data={mimsMetricChartOptions}
                        hasFilter={true}
                        headerStackProps={{
                            direction: { xs: 'column', md: 'column', lg: 'row' },
                            alignItems: { xs: 'stretch', lg: 'center' },
                        }}
                        childrenContainerProps={{
                            direction: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'stretch', sm: 'center' },
                            width: { xs: '100%', lg: 'auto' },
                            maxWidth: '100%',
                            pr: 0,
                            my: { xs: 1, md: 0 },
                        }}
                    >
                        <IncidentFilter
                            sectorName={chartPreference}
                            setSectorName={setChartPreference}
                            sectorOptionsList={['Actual MIMs', 'Preventive MIMs']}
                            handleChange={handleFilterChange}
                            type='MIMs'
                            disabled={false}
                            boxProps={{
                                sx: {
                                    position: 'static',
                                    minWidth: { xs: '100% !important', md: '100% !important', lg: '220px !important' },
                                    width: { xs: '100%', md: '100%', lg: '220px' }
                                }
                            }}
                        />
                    </MultiLineWidget>
                </Grid>
                <Grid item xs={12} sm={12} md={8} lg={8} id="mim-metrics">
                    <Box sx={{ backgroundColor: 'rgba(0, 55, 113, 0.2)', borderRadius: '5px', p: 2, minHeight: '340px' }}>
                        {gridLoader ? (
                            <LoaderComponent pt={16} />
                        ) : (
                            <CustomizedDataGrid
                                title={<TitleWithContext title="MIM Metrics" />}
                                tableColumns={getColumns()}
                                tableData={tableData}
                                tableType="Major_Incidents_Metrics"
                                defaultColumnNames={defaultColumnNames}
                                onSorting={handleSorting}
                                currSorting={sortingInfo}
                                listLength={tableData.length}
                                isDateRangeFilter={true}
                                isDisabled={false}
                                isRenderPagination={false}
                                isShowMoreButton={true}
                            >
                                <CaptureExportControls
                                    onScreenshot={() => {}}
                                    screenCapturing={false}
                                    onExport={handleExportMims}
                                    isExporting={isExportingMims}
                                    isVisible={true}
                                    showExport={true}
                                    type="snow"
                                    exportLabel="Export to Excel"
                                    sx={{
                                        ml: 0,
                                        '& #exclude-camera': {
                                            display: 'none',
                                        },
                                    }}
                                    exportsx={{borderRadius: '4px'}}
                                />
                            </CustomizedDataGrid>
                        )}
                    </Box>
                </Grid>

                <Grid item xs={12} md={4} lg={4} mt={2} id="problem-tickets" sx={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
                    <MultiLineWidget
                        title={<TitleWithContext title="Major Problem Tickets" />}
                        isLoading={problemMetricChartLoader}
                        data={problemMetricChartOptions}
                        hasFilter={true}
                        headerStackProps={{
                            direction: { xs: 'column', md: 'column', lg: 'row' },
                            alignItems: { xs: 'stretch', lg: 'center' },
                        }}
                        childrenContainerProps={{
                            direction: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'stretch', sm: 'center' },
                            width: { xs: '100%', lg: 'auto' },
                            maxWidth: '100%',
                            pr: 0,
                            my: { xs: 1, md: 0 },
                        }}
                    >
                    </MultiLineWidget>
                </Grid>
                <Grid item xs={12} sm={12} md={8} lg={8} mt={2} id="problem-tickets-metrics">
                    <Box sx={{ backgroundColor: 'rgba(0, 55, 113, 0.2)', borderRadius: '5px', p: 2, minHeight: '340px' }}>
                        {problemGridLoader ? (
                            <LoaderComponent pt={16} />
                        ) : (
                            <CustomizedDataGrid
                                title={<TitleWithContext title="Major Problem Tickets Metrics" />}
                                tableColumns={getProblemColumns()}
                                tableData={problemTableData}
                                tableType="Major_Problem_Tickets_Metrics"
                                defaultColumnNames={problemMetricsDefaultColumnNames}
                                onSorting={handleSorting}
                                currSorting={problemSortingInfo}
                                listLength={problemTableData.length}
                                isDateRangeFilter={true}
                                isDisabled={false}
                                isRenderPagination={false}
                                isShowMoreButton={true}
                            >
                                <CaptureExportControls
                                    onScreenshot={() => {}}
                                    screenCapturing={false}
                                    onExport={handleExportProblems}
                                    isExporting={isExportingProblems}
                                    isVisible={true}
                                    showExport={true}
                                    type="snow"
                                    exportLabel="Export to Excel"
                                    sx={{
                                        ml: 0,
                                        '& #exclude-camera': {
                                            display: 'none',
                                        },
                                    }}
                                    exportsx={{borderRadius: '4px'}}
                                />
                            </CustomizedDataGrid>
                        )}
                    </Box>
                </Grid>
                <Grid item xs={12} id="insights">
                    <Box marginTop={4} marginBottom={5}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '16px' }}>
                            <Typography component="div" variant="subHeader" sx={{ display: 'flex', alignItems: 'center', color: '#009BDB', mr: { xs: "1rem", sm: '2rem' } }}>
                                <TitleWithContext title="Insights/Actions" />
                            </Typography>
                            <Divider sx={{ flexGrow: 1, backgroundColor: '#009BDB', marginLeft: 2 }} />
                        </Box>
                        <Typography
                            variant="bodyText"
                            sx={{ fontStyle: 'italic', color: '#71758F' }}
                        >
                            You can display and update Insights/Actions if any.
                        </Typography>
                    </Box>
                    {loader ? (
                        <LoaderComponent />
                    ) : (
                        <Grid xs={12} sx={{ mb: 3 }}>
                            <Insights
                                insights={insights}
                                setInsights={setInsights}
                                onAdd={handleAddInsight}
                                onUpdate={handleUpdateInsight}
                                onDelete={handleDeleteInsight}
                            />
                        </Grid>
                    )}
                </Grid>
            </Grid>
        </PageWrapper>
    );
}

export default MIMReports;